import { Flex, Text, IconButton, Separator } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import Select, { SelectOption } from "@/components/shared/select";
import { EscalationRuleOperationType } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationRuleFormData } from "../../EscalationRuleForm";
import { useCallback, useMemo } from "react";

interface AssigneeSectionProps {
  value: EscalationRuleFormData;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  onRemove: () => void;
  selectedAgent: string;
  setSelectedAgent: (agent: string) => void;
  agentOptions: SelectOption[];
  loadingAgents: boolean;
}

export function AssigneeSection({
  value,
  onChange,
  onRemove,
  selectedAgent,
  setSelectedAgent,
  agentOptions,
  loadingAgents,
}: AssigneeSectionProps) {
  const handleAgentChange = useCallback(
    (agentId: string) => {
      setSelectedAgent(agentId);

      const selectedOption = agentOptions.find((opt) => opt.value === agentId);
      const agentExternalId = agentId;

      const existingAssigneeOperation = value.escalationRuleOperations.find(
        (operation) =>
          operation.type === EscalationRuleOperationType.ADD_ASSIGNEE
      );

      if (existingAssigneeOperation) {
        const updatedOperations = value.escalationRuleOperations.map(
          (operation) =>
            operation.type === EscalationRuleOperationType.ADD_ASSIGNEE
              ? {
                  ...operation,
                  key: agentExternalId,
                  value: selectedOption?.label || agentId,
                }
              : operation
        );
        onChange({ escalationRuleOperations: updatedOperations });
      } else {
        const newOperations = [
          ...value.escalationRuleOperations,
          {
            type: EscalationRuleOperationType.ADD_ASSIGNEE,
            enabled: true,
            key: agentExternalId,
            value: selectedOption?.label || agentId,
          },
        ];
        onChange({ escalationRuleOperations: newOperations });
      }
    },
    [value.escalationRuleOperations, onChange, agentOptions, setSelectedAgent]
  );

  return (
    <>
      <Separator
        orientation="horizontal"
        size="4"
        style={{ marginTop: "12px" }}
      />
      <Flex direction={"column"} gap={"2"} mt={"3"}>
        <Flex justify="between" align="center">
          <Text weight="bold" size="2">
            Assignee
          </Text>
          <IconButton color="gray" variant="ghost" size="1" onClick={onRemove}>
            <PlusIcon
              width={16}
              height={16}
              style={{ transform: "rotate(45deg)" }}
            />
          </IconButton>
        </Flex>
        <Text size="2" color="gray">
          Assign escalated tickets to a specific agent
        </Text>
        <Select
          options={agentOptions}
          value={selectedAgent}
          onChange={handleAgentChange}
          loading={loadingAgents}
          placeholder="Select an agent..."
        />
      </Flex>
    </>
  );
}
